import React, { useState } from "react";
import { Send, CheckCircle2, Loader2 } from "lucide-react";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { useToast } from "../hooks/use-toast";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TIME_OPTIONS = ["Morning", "Afternoon", "Evening", "Anytime"];

export default function ContactForm() {
  const { toast } = useToast();
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    best_time: "",
    reason: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast({ title: "Please add your name and email", variant: "destructive" });
      return;
    }
    setSubmitting(true);
    try {
      // 1) Save a backup copy to our database (best-effort; don't block on failure)
      fetch(`${API}/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      }).catch(() => {});

      // 2) Email the submission via FormSubmit — must be sent FROM THE BROWSER
      //    (FormSubmit rejects server-to-server calls). First submission triggers
      //    a one-time activation email to coachkam99@gmail.com.
      const res = await fetch("https://formsubmit.co/ajax/coachkam99@gmail.com", {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({
          _subject: "New contact form submission, Holistic Intuitive Nutrition",
          _captcha: "false",
          _template: "table",
          Name: form.name,
          Email: form.email,
          Phone: form.phone,
          "Best time to call/text": form.best_time,
          "Reason for contacting": form.reason,
        }),
      });
      const data = await res.json().catch(() => ({}));
      // FormSubmit returns success="false" for first-time activation emails, but that's still a successful submission
      // Only treat it as an error if the HTTP status is not OK
      if (!res.ok) throw new Error("Failed");
      setDone(true);
      toast({ title: "Message sent!", description: "Thank you, Coach Kam will be in touch soon." });
      setForm({ name: "", email: "", phone: "", best_time: "", reason: "" });
    } catch (err) {
      toast({
        title: "Something went wrong",
        description: "Please try again, or email coachkam99@gmail.com directly.",
        variant: "destructive",
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="text-center py-12">
        <CheckCircle2 className="mx-auto text-[var(--teal)] mb-4" size={48} />
        <h3 className="font-serif-brand text-3xl text-purple">Thank you!</h3>
        <p className="text-neutral-600 mt-3 max-w-md mx-auto">
          Your message has been sent to Coach Kam. She'll reach out to you at your preferred time.
        </p>
        <button onClick={() => setDone(false)} className="btn-brand btn-outline-teal mt-6">
          Send another message
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="space-y-5">
      <div className="grid md:grid-cols-2 gap-5">
        <div>
          <Label htmlFor="name" className="text-neutral-700 mb-1.5 block">
            Name <span className="text-[var(--purple)]">*</span>
          </Label>
          <Input
            id="name"
            value={form.name}
            onChange={(e) => update("name", e.target.value)}
            placeholder="Your full name"
            required
          />
        </div>
        <div>
          <Label htmlFor="email" className="text-neutral-700 mb-1.5 block">
            Email <span className="text-[var(--purple)]">*</span>
          </Label>
          <Input
            id="email"
            type="email"
            value={form.email}
            onChange={(e) => update("email", e.target.value)}
            placeholder="you@example.com"
            required
          />
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-5">
        <div>
          <Label htmlFor="phone" className="text-neutral-700 mb-1.5 block">
            Phone number
          </Label>
          <Input
            id="phone"
            type="tel"
            value={form.phone}
            onChange={(e) => update("phone", e.target.value)}
            placeholder="(000) 000-0000"
          />
        </div>
        <div>
          <Label className="text-neutral-700 mb-1.5 block">Best time to call or text</Label>
          <Select value={form.best_time} onValueChange={(v) => update("best_time", v)}>
            <SelectTrigger>
              <SelectValue placeholder="Select a time" />
            </SelectTrigger>
            <SelectContent>
              {TIME_OPTIONS.map((t) => (
                <SelectItem key={t} value={t}>
                  {t}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div>
        <Label htmlFor="reason" className="text-neutral-700 mb-1.5 block">
          Reason for contacting us
        </Label>
        <Textarea
          id="reason"
          value={form.reason}
          onChange={(e) => update("reason", e.target.value)}
          placeholder="Tell us a little about how we can help..."
          rows={5}
        />
      </div>

      <button type="submit" disabled={submitting} className="btn-brand btn-teal w-full md:w-auto">
        {submitting ? (
          <>
            <Loader2 size={16} className="animate-spin" /> Sending...
          </>
        ) : (
          <>
            <Send size={16} /> Send Message
          </>
        )}
      </button>
    </form>
  );
}
